import logger from '~/services/logger';
import {
  actionToComponentType,
  getIsClientLoaded,
  isLoadFeedFromClient,
  isServerAbortedFeed,
} from '~/services/ui-components/index.ts';
import type { Feed, UIComponent } from '~/services/layout/index.ts';
import ClientUiComponent from './client/client-ui-component.client.tsx';
import { ClientOnly } from 'remix-utils/client-only';
import { useLoaderData } from '@remix-run/react';
import { type loader } from '~/routes/_index.tsx';
import { LazyLoadOnScroll } from './lazy-load-on-scroll.tsx';
import { UIComponents } from './ui-components.tsx';
import type { UseIsLoggedInReturnType } from '~/hooks/use-is-logged-in.ts';
import { ClientUiComponentCacheFetch } from './client/client-ui-component-cache-fetch.client.tsx';
import { ClientFeedWrapper } from './client/client-feed-wrapper.tsx';

export type StorageFeedsData = {
  ['continue-watching']: {
    feed: Feed | undefined;
    isUpdating: boolean;
    isLoading: boolean;
  };
  favorites: {
    feed: Feed | undefined;
    isUpdating: boolean;
    isLoading: boolean;
  };
};
export type Props = {
  uiComponent: UIComponent;
  feed: Feed | null | undefined;
  isLoggedInData: UseIsLoggedInReturnType;
  storageFeedsData: StorageFeedsData;
  isTabsScreenClientComponent?: boolean;
};

export default function UiComponent({
  uiComponent,
  feed,
  isLoggedInData,
  storageFeedsData,
  isTabsScreenClientComponent = false,
}: Props): JSX.Element {
  const { defaultThemeV2Config, maximumCWentries } =
    useLoaderData<typeof loader>();
  const enableLazyLoadingOnScroll: boolean =
    defaultThemeV2Config.enable_lazy_loading_on_scroll;

  try {
    const isClientLoaded = getIsClientLoaded(uiComponent);

    const Component = UIComponents[uiComponent.component_type];

    if (!Component) return <></>;

    if (isClientLoaded && !!isLoggedInData.isLoggedIn) {
      const componentType = actionToComponentType[uiComponent.data?.source];

      const isFavoritesComponent: boolean = componentType === 'favorites';

      const isContinueWatchingComponent: boolean =
        componentType === 'continue-watching';

      const {
        favorites: { feed: favoritesFeed },
        'continue-watching': { feed: continueWatchingFeed },
      } = storageFeedsData;

      if (isFavoritesComponent || isContinueWatchingComponent) {
        const feed = isFavoritesComponent
          ? favoritesFeed
          : continueWatchingFeed;

        const isUpdating = isFavoritesComponent
          ? storageFeedsData.favorites.isUpdating
          : storageFeedsData['continue-watching'].isUpdating;

        const isLoading = isFavoritesComponent
          ? storageFeedsData.favorites.isLoading
          : storageFeedsData['continue-watching'].isLoading;

        if (isContinueWatchingComponent && feed?.entry) {
          const isDropTrackingOfOlderEntries: boolean =
            Array.isArray(feed?.entry) && feed.entry.length > maximumCWentries;

          if (isDropTrackingOfOlderEntries)
            feed.entry = feed.entry.slice(0, maximumCWentries);
        }

        return (
          <Component
            uiComponent={uiComponent}
            isClient={true}
            isLoading={isUpdating ? false : isLoading}
            feed={feed}
            isClientStorage={true}
            storageFeedsData={storageFeedsData}
            isLoggedInData={isLoggedInData}
          />
        );
      }
    }

    if (isTabsScreenClientComponent) {
      return (
        <ClientOnly fallback={<></>}>
          {() => (
            <ClientUiComponentCacheFetch
              Component={Component}
              uiComponent={uiComponent}
              storageFeedsData={storageFeedsData}
              isLoggedInData={isLoggedInData}
            />
          )}
        </ClientOnly>
      );
    }

    const feedIsServerAborted: boolean = isServerAbortedFeed(feed);
    const isClientUiComponent: boolean =
      isLoadFeedFromClient(uiComponent) || feedIsServerAborted;

    if (isClientUiComponent) {
      return (
        <ClientOnly fallback={<></>}>
          {() => (
            <ClientUiComponent
              Component={Component}
              uiComponent={uiComponent}
              storageFeedsData={storageFeedsData}
              isLoggedInData={isLoggedInData}
              forceFresh={feedIsServerAborted}
            />
          )}
        </ClientOnly>
      );
    }

    if (!feed) return <></>;

    return (
      <ClientFeedWrapper
        uiComponent={uiComponent}
        feed={feed}
        render={({ feed: renderFeed, isClient, isLoading }) => {
          const componentProps = {
            uiComponent,
            feed: renderFeed,
            storageFeedsData,
            isLoggedInData,
            isClient,
            isLoading,
          };

          if (enableLazyLoadingOnScroll) {
            return (
              <LazyLoadOnScroll
                componentType={uiComponent.component_type}
                props={componentProps}
              />
            );
          }

          return <Component {...componentProps} />;
        }}
      />
    );
  } catch (error: any) {
    logger.info(`UiComponent: ${error.message}`);
    return <></>;
  }
}
